import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useToast } from "@/components/ui/toast-provider"

import { messageFromUnknown } from "@/lib/errors"
import { isMissingDatabaseColumn, isMissingDatabaseRelation } from "@/lib/supabase-relation-errors"
// Phase 1/2: `populateDocumentsFromVisa` was removed from dashboard workspace creation — visa
// requirements are seeded only via `provisionWorkspace` → `populateVisaApplicationTaskFromVisaMetadata`.
import { provisionWorkspace } from "@/lib/provision-workspace"
import { toastPartialWorkspaceSeed } from "@/lib/toast-partial-workspace-seed"
import { decrementWorkspaceCount } from "@/lib/profile-usage"
import { getCountryByCode } from "@/lib/countries"
import { WorkspaceLimitError } from "@/lib/usage-limits"
import { useUpgradePrompt } from "@/providers/upgrade-prompt-context"
import { normalizeIsoCurrency } from "@/lib/app-currency"
import { convertThroughUsd, type UsdPerUnitMap } from "@/lib/currency-convert"
import { loadUsdPerUnitFromDbOrFrankfurter } from "@/lib/fx-load"
import { getWorkspaceAccess } from "@/lib/workspace-access"
import { useAuth } from "@/lib/auth"
import { supabase } from "@/lib/supabase"
import type { WorkspaceLifecycleStatus, WorkspaceRow, WorkspaceWithProgress } from "@/types/workspace"

function localTodayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function workspaceLifecycleStatus(w: WorkspaceRow): WorkspaceLifecycleStatus {
  return w.status ?? "active"
}

function maxWorkspaceMembers(): number {
  const raw = import.meta.env.VITE_MAX_WORKSPACE_MEMBERS
  const n = raw !== undefined ? Number(raw) : 50
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 50
}

/** Rows from `workspace_dashboard_list_stats` (Postgres `bigint` may arrive as string in JSON). */
type DashboardRpcRow = {
  workspace_id: string
  task_total: number | string | null
  task_done: number | string | null
  overdue_task_count: number | string | null
  next_task_due_date: string | null
  next_task_title: string | null
  next_itinerary_date: string | null
  next_itinerary_title: string | null
}

type TaskCostLite = {
  workspace_id: string
  cost: number | string | null
  currency: string | null
}

function coerceCount(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.floor(v))
  if (typeof v === "string") {
    const n = Number(v)
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n))
  }
  return 0
}

/** Same ordering as legacy `computeWorkspaceTaskStats`: soonest ISO date, then title. */
function pickSoonestDeadline(
  a: { dateIso: string; title: string } | null,
  b: { dateIso: string; title: string } | null,
): { dateIso: string; title: string } | null {
  if (!a) return b
  if (!b) return a
  const cmp = a.dateIso.localeCompare(b.dateIso)
  if (cmp < 0) return a
  if (cmp > 0) return b
  return a.title.localeCompare(b.title) <= 0 ? a : b
}

function buildWorkspaceDashboardProgressMap(
  rows: DashboardRpcRow[] | null,
  workspaceIds: string[],
): Map<
  string,
  {
    computedProgress: number
    taskTotal: number
    taskDone: number
    overdueTaskCount: number
    nextDeadline: { dateIso: string; title: string } | null
  }
> {
  const byId = new Map<string, DashboardRpcRow>()
  for (const r of rows ?? []) {
    byId.set(String(r.workspace_id), r)
  }

  const out = new Map<
    string,
    {
      computedProgress: number
      taskTotal: number
      taskDone: number
      overdueTaskCount: number
      nextDeadline: { dateIso: string; title: string } | null
    }
  >()

  for (const id of workspaceIds) {
    const row = byId.get(id)
    const taskTotal = row ? coerceCount(row.task_total) : 0
    const taskDone = row ? coerceCount(row.task_done) : 0
    const overdueTaskCount = row ? coerceCount(row.overdue_task_count) : 0
    const computedProgress = taskTotal === 0 ? 0 : Math.round((taskDone / taskTotal) * 100)

    const taskCand =
      row?.next_task_due_date && String(row.next_task_due_date).length > 0
        ? {
            dateIso: String(row.next_task_due_date).slice(0, 10),
            title: (row.next_task_title ?? "").trim(),
          }
        : null
    const itCand =
      row?.next_itinerary_date && String(row.next_itinerary_date).length > 0
        ? {
            dateIso: String(row.next_itinerary_date).slice(0, 10),
            title: (row.next_itinerary_title ?? "").trim(),
          }
        : null

    out.set(id, {
      computedProgress,
      taskTotal,
      taskDone,
      overdueTaskCount,
      nextDeadline: pickSoonestDeadline(taskCand, itCand),
    })
  }

  return out
}

type BudgetRowLite = {
  workspace_id: string
  total_budget: number | string | null
  currency: string | null
}

/**
 * Budget use % after converting task costs into each workspace budget currency.
 * Returns `null` when there is no usable budget, FX is unavailable for a needed
 * conversion, or every positive-cost task fails conversion (avoids silent $0).
 */
function computeBudgetUsedPctByWorkspace(
  workspaceIds: string[],
  taskCosts: TaskCostLite[] | null,
  budgetRows: BudgetRowLite[] | null,
  usdPerUnit: UsdPerUnitMap | null,
): Map<string, number | null> {
  const result = new Map<string, number | null>()
  for (const id of workspaceIds) result.set(id, null)
  if (taskCosts == null || !budgetRows?.length) return result

  const budgetByWs = new Map<string, { total: number; currency: string }>()
  for (const b of budgetRows) {
    const wid = String(b.workspace_id)
    const total = typeof b.total_budget === "number" ? b.total_budget : Number(b.total_budget)
    const cur = normalizeIsoCurrency(b.currency)
    if (!cur || !Number.isFinite(total) || total <= 0) continue
    budgetByWs.set(wid, { total, currency: cur })
  }

  const costsByWs = new Map<string, TaskCostLite[]>()
  for (const row of taskCosts) {
    const wid = String(row.workspace_id)
    const list = costsByWs.get(wid)
    if (list) list.push(row)
    else costsByWs.set(wid, [row])
  }

  for (const id of workspaceIds) {
    const budget = budgetByWs.get(id)
    if (!budget) continue
    const rows = costsByWs.get(id) ?? []
    let sum = 0
    let convertible = 0
    let failed = 0
    for (const row of rows) {
      const raw = row.cost
      const n = typeof raw === "number" ? raw : Number(raw)
      if (!Number.isFinite(n) || n <= 0) continue
      const from = normalizeIsoCurrency(row.currency) ?? budget.currency
      if (from === budget.currency) {
        sum += n
        convertible += 1
        continue
      }
      if (!usdPerUnit) {
        failed += 1
        continue
      }
      const conv = convertThroughUsd(n, from, budget.currency, usdPerUnit)
      if (conv == null) {
        failed += 1
        continue
      }
      sum += conv
      convertible += 1
    }
    // Mixed currencies without rates: do not invent a %.
    if (convertible === 0 && failed > 0) continue
    if (failed > 0 && !usdPerUnit) continue
    result.set(id, (sum / budget.total) * 100)
  }

  return result
}

async function collaboratorCountByWorkspace(
  client: typeof supabase,
  ownerWorkspaceIds: string[]
): Promise<Map<string, number>> {
  const map = new Map<string, number>()
  if (ownerWorkspaceIds.length === 0) return map
  const { data, error } = await client
    .from("workspace_members")
    .select("workspace_id")
    .in("workspace_id", ownerWorkspaceIds)
  if (error) {
    if (isMissingDatabaseRelation(error)) return map
    return map
  }
  if (!data) return map
  for (const row of data as { workspace_id: string }[]) {
    const id = row.workspace_id
    map.set(id, (map.get(id) ?? 0) + 1)
  }
  return map
}

export function useWorkspaces() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const { openUpgrade } = useUpgradePrompt()
  const { user } = useAuth()
  const [workspaces, setWorkspaces] = useState<WorkspaceWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const createInFlightRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setWorkspaces([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data: ownedRows, error: ownedErr } = await supabase
        .from("workspaces")
        .select("*")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false })

      if (ownedErr) throw ownedErr

      const { data: memberRows, error: memErr } = await supabase
        .from("workspace_members")
        .select("workspace_id")
        .eq("user_id", user.id)

      let memberIds: string[] = []
      if (memErr) {
        if (!isMissingDatabaseRelation(memErr)) throw memErr
      } else {
        memberIds = [
          ...new Set((memberRows ?? []).map((r) => (r as { workspace_id: string }).workspace_id)),
        ]
      }
      let sharedRows: WorkspaceRow[] = []
      if (memberIds.length > 0) {
        const { data: s, error: sErr } = await supabase.from("workspaces").select("*").in("id", memberIds)
        if (sErr) throw sErr
        sharedRows = (s ?? []) as WorkspaceRow[]
      }

      const ownedList = (ownedRows ?? []) as WorkspaceRow[]
      const ownedIdSet = new Set(ownedList.map((w) => w.id))
      const sharedOnly = sharedRows.filter((w) => !ownedIdSet.has(w.id))

      const collabMap = await collaboratorCountByWorkspace(
        supabase,
        ownedList.map((w) => w.id)
      )

      const list: Array<WorkspaceRow & { accessRole: "owner" | "member" }> = [
        ...ownedList.map((w) => ({ ...w, accessRole: "owner" as const })),
        ...sharedOnly.map((w) => ({ ...w, accessRole: "member" as const })),
      ]

      if (list.length === 0) {
        setWorkspaces([])
        return
      }

      const ids = list.map((w) => w.id)
      const today = localTodayIso()

      const [dashRes, taskCostRes, budgetRes] = await Promise.all([
        supabase.rpc("workspace_dashboard_list_stats", {
          p_workspace_ids: ids,
          p_today: today,
        }),
        supabase
          .from("workspace_tasks")
          .select("workspace_id, cost, currency")
          .in("workspace_id", ids)
          .not("cost", "is", null)
          .gt("cost", 0),
        supabase.from("workspace_budget").select("workspace_id, total_budget, currency").in("workspace_id", ids),
      ])

      const dashErr = dashRes.error
      const taskCostErr = taskCostRes.error
      const budgetError = budgetRes.error

      if (dashErr) {
        toast.error(t("workspaces.toast.taskStatsFailed", { message: dashErr.message }))
      }
      if (budgetError && !isMissingDatabaseRelation(budgetError)) {
        toast.error(t("workspaces.toast.budgetStatsFailed", { message: budgetError.message }))
      }

      const statsMap = buildWorkspaceDashboardProgressMap(
        dashErr ? null : ((dashRes.data ?? []) as DashboardRpcRow[]),
        ids,
      )

      let usdPerUnit: UsdPerUnitMap | null = null
      const budgetRows = budgetError ? null : ((budgetRes.data ?? []) as BudgetRowLite[])
      const taskCosts = taskCostErr ? null : ((taskCostRes.data ?? []) as TaskCostLite[])
      const needsFx =
        Boolean(budgetRows?.length) &&
        Boolean(taskCosts?.some((row) => {
          const budget = budgetRows?.find((b) => String(b.workspace_id) === String(row.workspace_id))
          const budgetCur = normalizeIsoCurrency(budget?.currency ?? null)
          const taskCur = normalizeIsoCurrency(row.currency)
          return Boolean(budgetCur && taskCur && budgetCur !== taskCur)
        }))
      if (needsFx) {
        try {
          usdPerUnit = await loadUsdPerUnitFromDbOrFrankfurter(supabase)
        } catch {
          usdPerUnit = null
        }
      }

      const budgetUsage = computeBudgetUsedPctByWorkspace(ids, taskCosts, budgetRows, usdPerUnit)

      const merged: WorkspaceWithProgress[] = list.map((w) => {
        const s = statsMap.get(w.id)!
        return {
          ...w,
          computedProgress: s.computedProgress,
          taskTotal: s.taskTotal,
          taskDone: s.taskDone,
          overdueTaskCount: s.overdueTaskCount,
          nextDeadline: s.nextDeadline,
          accessRole: w.accessRole,
          collaboratorCount: w.accessRole === "owner" ? collabMap.get(w.id) ?? 0 : 0,
          budgetUsedPct: budgetUsage.get(w.id) ?? null,
        }
      })

      setWorkspaces(merged)
    } catch (e) {
      const message = messageFromUnknown(e) || t("workspaces.toast.loadFailed")
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [user, t, toast])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const createWorkspace = useCallback(
    async (input: {
      nationalityCode: string
      originCode: string
      destinationCode: string
      tripGoal: string
      hasVisa: boolean
      visaType: string | null
      visaStatus?: string | null
      budget?: number
      budgetCurrency?: string
      workspaceName?: string
    }) => {
      if (!user?.id) {
        toast.error(t("workspaces.toast.loginRequired"))
        return null
      }
      if (createInFlightRef.current) {
        return null
      }
      createInFlightRef.current = true
      try {
        const nat = input.nationalityCode.trim().toUpperCase()
        const origin = input.originCode.trim().toUpperCase()
        const dest = input.destinationCode.trim().toUpperCase()
        if (!nat || !origin || !dest) {
          toast.error(t("workspaces.toast.missingCountries"))
          return null
        }

        const international = origin !== dest
        const hasExistingVisa = !international || input.hasVisa
        /** Seeding uses `visaType` only when `!hasExistingVisa`; do not default to `visitor` there (canonical row must match). */
        const visaType = hasExistingVisa
          ? input.visaType?.trim() || "visitor"
          : (input.visaType?.trim() ?? "")

        if (international && !input.hasVisa && !input.visaType?.trim()) {
          toast.error(t("workspaces.toast.visaTypeRequired"))
          return null
        }

        const destinationLabel =
          getCountryByCode(dest)?.name ?? dest

        let workspaceId: string | null = null
        let seedingError: string | undefined
        try {
          /** Route `visa_status` strings must not be passed as `visaStatus` when the user is applying — they are not user intent. */
          const visaStatusForProvision =
            international && !input.hasVisa ? "applying_new" : (input.visaStatus ?? null)

          const result = await provisionWorkspace(
            {
              userId: user.id,
              nationalityCode: nat,
              originCode: origin,
              destinationCode: dest,
              destination: destinationLabel,
              visaType,
              hasExistingVisa,
              goal: input.tripGoal,
              budget: input.budget,
              ...(input.budgetCurrency?.trim() ? { budgetCurrency: input.budgetCurrency.trim() } : {}),
              workspaceName: input.workspaceName?.trim() || undefined,
              visaStatus: visaStatusForProvision,
              locale: typeof navigator !== "undefined" ? navigator.language : undefined,
            },
            supabase
          )
          workspaceId = result.workspaceId
          seedingError = result.error
        } catch (e) {
          if (e instanceof WorkspaceLimitError) {
            openUpgrade(t("upgrade.featureWorkspace"))
            return null
          }
          if (e instanceof Error) {
            toast.error(e.message)
          }
          return null
        }

        if (workspaceId && seedingError) {
          toastPartialWorkspaceSeed(t, navigate, workspaceId, seedingError)
        } else if (workspaceId) {
          toast.success(t("workspaces.toast.created"))
        }
        await refresh()
        return workspaceId
      } finally {
        createInFlightRef.current = false
      }
    },
    [refresh, t, user, toast, openUpgrade, navigate]
  )

  const deleteWorkspace = useCallback(
    async (id: string) => {
      if (!user?.id) {
        toast.error(t("workspaces.toast.loginRequired"))
        return
      }

      const role = await getWorkspaceAccess(supabase, user.id, id)
      if (role !== "owner") {
        toast.error(t("workspaces.toast.onlyOwnerDelete"))
        return
      }

      const { error: deleteError } = await supabase
        .from("workspaces")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id)

      if (deleteError) {
        toast.error(deleteError.message)
        throw deleteError
      }

      try {
        await decrementWorkspaceCount(supabase, user.id)
      } catch {
        // intentional: count sync is best-effort; workspace is already deleted
      }

      toast.success(t("workspaces.toast.deleted"))
      await refresh()
    },
    [refresh, t, user, toast]
  )

  const updateWorkspaceStatus = useCallback(
    async (id: string, status: WorkspaceLifecycleStatus) => {
      if (!user?.id) {
        toast.error(t("workspaces.toast.loginRequired"))
        return
      }
      const role = await getWorkspaceAccess(supabase, user.id, id)
      if (role !== "owner") {
        toast.error(t("workspaces.toast.onlyOwnerStatus"))
        return
      }
      const { error: upErr } = await supabase
        .from("workspaces")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("user_id", user.id)
      if (upErr) {
        if (isMissingDatabaseColumn(upErr, ["status"])) {
          toast.error(t("workspaces.toast.statusColumnMissing"))
          return
        }
        toast.error(upErr.message)
        throw upErr
      }
      if (status === "completed") {
        toast.success(t("workspaces.toast.statusDone"))
      } else {
        toast.success(t("workspaces.toast.statusUpdated"))
      }
      await refresh()
    },
    [refresh, t, user, toast]
  )

  const addWorkspaceMember = useCallback(
    async (workspaceId: string, memberUserId: string) => {
      if (!user?.id) {
        toast.error(t("workspaces.toast.loginRequired"))
        return
      }
      const role = await getWorkspaceAccess(supabase, user.id, workspaceId)
      if (role !== "owner") {
        toast.error(t("workspaces.toast.onlyOwnerMembers"))
        return
      }
      const trimmed = memberUserId.trim()
      if (!trimmed) {
        toast.error(t("workspaces.toast.enterMemberUuid"))
        return
      }
      if (trimmed === user.id) {
        toast.error(t("workspaces.toast.alreadyOwner"))
        return
      }

      const maxM = maxWorkspaceMembers()
      const current = (await collaboratorCountByWorkspace(supabase, [workspaceId])).get(workspaceId) ?? 0
      if (current >= maxM) {
        toast.error(t("workspaces.toast.memberLimit", { max: String(maxM) }))
        return
      }

      const { error: insErr } = await supabase.from("workspace_members").insert({
        workspace_id: workspaceId,
        user_id: trimmed,
      })
      if (insErr) {
        if (isMissingDatabaseRelation(insErr)) {
          toast.error(t("workspaces.toast.membersTableMissingShort"))
          return
        }
        toast.error(insErr.message)
        throw insErr
      }
      toast.success(t("workspaces.toast.memberAdded"))
      await refresh()
    },
    [refresh, t, user, toast]
  )

  const removeWorkspaceMember = useCallback(
    async (workspaceId: string, memberUserId: string) => {
      if (!user?.id) return
      const role = await getWorkspaceAccess(supabase, user.id, workspaceId)
      if (role !== "owner" && memberUserId !== user.id) {
        toast.error(t("workspaces.toast.noPermission"))
        return
      }
      const { error: delErr } = await supabase
        .from("workspace_members")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("user_id", memberUserId)
      if (delErr) {
        if (isMissingDatabaseRelation(delErr)) {
          toast.error(t("workspaces.toast.membersTableMissing"))
          return
        }
        toast.error(delErr.message)
        throw delErr
      }
      toast.success(t("workspaces.toast.memberRemoved"))
      await refresh()
    },
    [refresh, t, user, toast]
  )

  return {
    workspaces,
    loading,
    error,
    refresh,
    createWorkspace,
    deleteWorkspace,
    updateWorkspaceStatus,
    addWorkspaceMember,
    removeWorkspaceMember,
    workspaceLifecycleStatus,
  }
}
